import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';

import { Footer } from '@/components/Layout/Footer';

import { Outlet } from 'react-router-dom';
import { getHomeMetaDescByLocale, getHtmlLangByLocale } from '@/shared/utility';
import { Zendesk } from '@/components/Zendesk';
import { Spelpaus } from '@/components/Spelpaus';
import {
  APP_VERSION,
  DATE_FORMAT_CANADA_LOCALES,
  envIsCanada,
  envIsMexico,
  envIsNormal,
  envIsSweden,
} from '@/constants/constants';
import { useStatsAndLimits } from '@/hooks/useStatsAndLimits';
import { SearchPalette } from '@/components/SearchPalette';
import { selectIsAuthenticated } from '@/store/selectors/authSelectors';
import { useConsents } from '@/hooks/useConsents';
import { useOntarioLoginMigration } from '@/hooks/useOntarioLoginMigration';
import { useAlbertaLoginMigration } from '@/hooks/useAlbertaLoginMigration';
import { useIncompleteDataCheck } from '@/hooks/useIncompleteDataCheck';
import { useRequiredDepositLimitsCheck } from '@/hooks/useRequiredDepositLimitsCheck';
import { usePepHioHook } from '@/hooks/usePepHioHook';
import { mapLocaleToLang } from '@/utils/utils';
import { Settings } from 'luxon';

import { autoLogin } from '@/store/actions/autoLoginActions';
import { selectLocale } from '@/store/selectors/commonSelectors';
import HelmetAlternateLinks from '@/features/helmet/HelmetAlternateLinks';
import CookieConsentManager from '@/features/cookie-consent/CookieConsentManager';
import DrawerContainer from '@/features/drawers/DrawerContainer';
import TabBar from '@/components/Layout/TabBar';
import GlobalStyles from '@/components/Common/GlobalStyles';
import { useRewardsWheelReminder } from '@/hooks/useRewardsWheelReminder';
import { selectDocumentTitle } from '@/store/cms/welcome-texts/selectors';
import { useIntl } from 'react-intl';
import FluidPaymentsWrapper from '@/features/cashier/fluid/FluidPaymentsWrapper';
import { useWelcomeOffersDisplay } from '@/hooks/useWelcomeOffersDisplay';
import { useSuccessfulExperience } from '@/hooks/useRateAppEvent';
import { useWebNotifications } from '@/hooks/useWebNotifications';
import { GeolocationManager } from '@/hooks/useGeolocation';
import { useInjectSmartBannerScript } from '@/hooks/useInjectSmartBannerScript';
import Sidebar from '@/components/shared/sidebar/Sidebar';
import Header from '@/components/shared/header/Header';
import { useAffinityId } from '@/hooks/useAffinityId';
import { ModalContainer } from '@/features/modal/ModalContainer';
import { useBiometricLogin } from '@/hooks/useBiometricLogin';
import { SeonGate } from '@/hooks/useSeon';

function App(): JSX.Element {
  const locale = useSelector(selectLocale);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const windowOrigin = window.location.origin;
  const windowPathname = window.location.pathname;
  const documentTitle = useSelector(selectDocumentTitle);
  const dispatch = useDispatch();
  const intl = useIntl();

  const title =
    intl.formatMessage({ id: 'document-title.base' }) +
    (documentTitle ? ` | ${documentTitle}` : '');

  useEffect(() => {
    Settings.defaultLocale = mapLocaleToLang(locale);
  }, [locale]);

  useStatsAndLimits();
  useConsents();
  useOntarioLoginMigration();
  useAlbertaLoginMigration();
  useIncompleteDataCheck();
  useRequiredDepositLimitsCheck();
  usePepHioHook();
  useRewardsWheelReminder();
  useWelcomeOffersDisplay();
  useSuccessfulExperience();
  useWebNotifications({
    enabled:
      envIsMexico() ||
      envIsCanada() ||
      (envIsNormal() && DATE_FORMAT_CANADA_LOCALES.includes(locale)),
  });
  useInjectSmartBannerScript();
  useAffinityId();
  useBiometricLogin();

  useEffect(() => {
    // Initiate auto login flow
    dispatch(autoLogin());
  }, []);

  useEffect(() => {
    // Scroll to top of page when the user is logging in
    if (isAuthenticated) {
      window.scrollTo(0, 0);
    }
  }, [isAuthenticated]);

  return (
    <>
      <Helmet>
        <html lang={getHtmlLangByLocale(locale)} />
        <title>{title}</title>
        <meta name="description" content={getHomeMetaDescByLocale(locale)} />
        {APP_VERSION && <meta name="luckydays_version" content={APP_VERSION} />}
        {envIsCanada() && isAuthenticated && (
          <>
            <script
              type="text/javascript"
              src="https://servicesbl.luckydays.ca/v4/html5/?version=v3"
            ></script>
            <script type="text/javascript" src="/toolkit.js"></script>
          </>
        )}
        <link rel="canonical" href={windowOrigin + windowPathname} />
      </Helmet>
      <HelmetAlternateLinks locale={locale} path={windowPathname} origin={windowOrigin} />
      <GlobalStyles />
      {envIsSweden() && <Spelpaus />}

      <main className="min-h-screen relative">
        <div className="hidden lg:block lg:fixed lg:left-0 lg:top-0 lg:z-[51] lg:h-screen">
          <Sidebar />
        </div>
        <Header />
        <div className="lg:ml-[80px] relative z-[1]">
          <Outlet />
          <Footer />
        </div>
      </main>
      <CookieConsentManager />
      <TabBar />
      <DrawerContainer />
      <ModalContainer />
      <SearchPalette />
      {envIsCanada() && <SeonGate />}
      <FluidPaymentsWrapper />
      <GeolocationManager />
      <Zendesk locale={locale} />
    </>
  );
}

export default App;
